import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Divider,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2), maxWidth: 520 },
  backButton: { marginRight: theme.spacing(1) },
  paper: {
    padding: theme.spacing(3),
    borderRadius: 12,
    boxShadow: theme.shadows[2],
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    "&::before": {
      content: '""',
      width: 4,
      height: 20,
      backgroundColor: theme.palette.primary.main,
      marginRight: theme.spacing(1),
      borderRadius: 2,
    },
  },
  field: { marginBottom: theme.spacing(2) },
  addRow: { display: "flex", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  coverSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "block",
    width: "100%",
  },
  coverPlaceholder: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    border: "2px dashed",
    borderColor: theme.palette.grey[400],
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: theme.palette.grey[50],
    minHeight: 120,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main + "08",
    },
  },
  coverPreview: {
    width: "100%",
    maxWidth: 280,
    maxHeight: 160,
    objectFit: "cover",
    borderRadius: 12,
    marginTop: theme.spacing(1),
  },
  coverActions: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginTop: theme.spacing(1) },
  inputFile: { display: "none" },
  submit: { marginTop: theme.spacing(3) },
}));

const GroupNew = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [newNumber, setNewNumber] = useState("");
  const [participants, setParticipants] = useState([]);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const coverInputRef = useRef(null);

  useEffect(() => {
    if (!connectionId) history.replace("/group-management");
  }, [connectionId, history]);

  const addParticipant = () => {
    const n = newNumber.replace(/\D/g, "").trim();
    if (!n) return;
    if (participants.includes(n)) return;
    setParticipants((prev) => [...prev, n]);
    setNewNumber("");
  };

  const removeParticipant = (num) => {
    setParticipants((prev) => prev.filter((p) => p !== num));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(i18n.t("groupManagement.coverMustBeImage"));
      return;
    }
    setCoverFile(file);
    const reader = new FileReader();
    reader.onload = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const clearCover = () => {
    setCoverFile(null);
    setCoverPreview(null);
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!connectionId || !name.trim()) {
      toastError(new Error(i18n.t("groupManagement.nameRequired")));
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("participants", JSON.stringify(participants));
      if (description.trim()) formData.append("description", description.trim());
      if (coverFile) formData.append("cover", coverFile);
      await api.post(`/group-management/connections/${connectionId}/groups`, formData);
      toast.success(i18n.t("groupManagement.groupCreated"));
      history.push(`/group-management/groups?connectionId=${connectionId}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!connectionId) return null;

  return (
    <MainContainer>
      <MainHeader>
        <Button className={classes.backButton} onClick={() => history.push("/group-management")} startIcon={<ArrowBackIcon />}>
          {i18n.t("groupManagement.back")}
        </Button>
        <Title>{i18n.t("groupManagement.createGroup")}</Title>
      </MainHeader>

      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <form onSubmit={handleSubmit}>
            <Typography className={classes.sectionTitle} style={{ marginTop: 0 }}>
              {i18n.t("groupManagement.groupName")} *
            </Typography>
            <TextField
              className={classes.field}
              fullWidth
              placeholder={i18n.t("groupManagement.groupNamePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              variant="outlined"
              size="small"
              required
            />

            <Typography className={classes.sectionTitle}>
              {i18n.t("groupManagement.groupDescription")}
            </Typography>
            <TextField
              className={classes.field}
              fullWidth
              multiline
              rows={3}
              placeholder={i18n.t("groupManagement.groupDescriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="outlined"
              size="small"
            />

            <Typography className={classes.sectionTitle}>
              {i18n.t("groupManagement.groupCoverPhoto")}
            </Typography>
            <div className={classes.coverSection}>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className={classes.inputFile}
                onChange={handleCoverChange}
                id="group-cover-input"
              />
              {!coverPreview ? (
                <label htmlFor="group-cover-input" className={classes.coverPlaceholder}>
                  <Box display="flex" flexDirection="column" alignItems="center" py={1}>
                    <PhotoCameraIcon style={{ fontSize: 40, color: "inherit" }} />
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                      {i18n.t("groupManagement.addCoverPhoto")}
                    </Typography>
                  </Box>
                </label>
              ) : (
                <Box>
                  <img src={coverPreview} alt="Capa" className={classes.coverPreview} />
                  <div className={classes.coverActions}>
                    <Typography variant="body2" color="textSecondary" noWrap style={{ flex: 1 }}>
                      {coverFile?.name}
                    </Typography>
                    <Button size="small" onClick={clearCover}>
                      {i18n.t("groupManagement.removeCover")}
                    </Button>
                  </div>
                </Box>
              )}
            </div>

            <Divider style={{ margin: "24px 0 16px" }} />

            <Typography className={classes.sectionTitle}>
              {i18n.t("groupManagement.participants")}
            </Typography>
            <div className={classes.addRow}>
              <TextField
                fullWidth
                size="small"
                placeholder="5511999999999"
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addParticipant())}
                variant="outlined"
              />
              <IconButton color="primary" onClick={addParticipant} size="medium">
                <AddIcon />
              </IconButton>
            </div>
            <List dense>
              {participants.map((num) => (
                <ListItem key={num}>
                  <ListItemText primary={num} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => removeParticipant(num)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            <Button className={classes.submit} type="submit" variant="contained" color="primary" disabled={loading} fullWidth>
              {loading ? "..." : i18n.t("groupManagement.create")}
            </Button>
          </form>
        </Paper>
      </Box>
    </MainContainer>
  );
};

export default GroupNew;
